import React, { useEffect, useMemo, useState } from "react";
import { GraduationCap, ChevronLeft, ChevronRight, CheckCircle2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getRoleFeatures } from "@/pages/navConfigs";
import { PageCard, PillBtn } from "./common";

export default function LearnPage() {
  const { user, customRole } = useAuth();
  const features = useMemo(() => (customRole?.features || getRoleFeatures(user.role)).join(","), [user, customRole]);
  const [course, setCourse] = useState(null);
  const [i, setI] = useState(0);
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    api.get("/learn/course", { params: { features } }).then((r) => {
      setCourse(r.data);
      const firstOpen = r.data.lessons.findIndex((l) => !r.data.done.includes(l.key));
      setI(firstOpen < 0 ? 0 : firstOpen);
    }).catch(() => toast.error("Could not load the course"));
  }, [features]);
  useEffect(() => setImgOk(true), [i]);

  if (!course) return <Layout><div className="p-12 text-slate-400">Loading course…</div></Layout>;
  const lessons = course.lessons;
  const lesson = lessons[i];
  const done = new Set(course.done);
  const last = i === lessons.length - 1;

  const markDone = async () => {
    if (!done.has(lesson.key)) {
      await api.post("/learn/progress", { key: lesson.key });
      setCourse((c) => ({ ...c, done: [...c.done, lesson.key] }));
    }
  };
  const next = async () => { await markDone(); setI((x) => Math.min(x + 1, lessons.length - 1)); };
  const complete = async () => {
    await markDone();
    try {
      const r = await api.post("/learn/complete", { lesson_keys: lessons.map((l) => l.key) });
      setCourse((c) => ({ ...c, completed_at: r.data.completed_at, done: lessons.map((l) => l.key) }));
      toast.success("Course completed! Your completion report was sent to support.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 max-w-5xl mx-auto">
        <PageCard Icon={GraduationCap} title="Learn Dashboard" subtitle={`${lessons.length} short lessons on the features you use. Go through each with Next, then Complete.`} testid="learn-page" />
        {course.completed_at && <div className="rounded-2xl bg-emerald-50 text-emerald-800 px-5 py-3 mb-4 flex items-center gap-2 font-semibold" data-testid="learn-completed-banner"><PartyPopper className="w-5 h-5" /> You completed this course on {new Date(course.completed_at).toLocaleDateString()}. Feel free to revisit any lesson.</div>}
        <div className="grid lg:grid-cols-[240px_1fr] gap-5">
          <ol className="bg-white rounded-2xl border border-slate-100 p-2 h-fit max-h-[70vh] overflow-y-auto" data-testid="learn-outline">
            {lessons.map((l, j) => (
              <li key={l.key}><button onClick={() => setI(j)} className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${j === i ? "bg-sky-50 font-semibold" : "hover:bg-slate-50"}`} data-testid={`learn-step-${l.key}`}>
                {done.has(l.key) ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <span className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}<span className="truncate">{l.title}</span>
              </button></li>
            ))}
          </ol>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6" data-testid="learn-lesson">
            <div className="text-xs font-bold text-sky-600 uppercase tracking-wider">Lesson {i + 1} of {lessons.length}</div>
            <h2 className="font-display font-black text-2xl mt-1 mb-3" data-testid="learn-lesson-title">{lesson.title}</h2>
            <p className="text-slate-700 leading-relaxed mb-5">{lesson.body}</p>
            {imgOk && <img src={lesson.image} alt={lesson.title} onError={() => setImgOk(false)} className="rounded-2xl border border-slate-100 shadow-sm w-full mb-5" data-testid="learn-lesson-image" />}
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-5"><div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all" style={{ width: `${(done.size / lessons.length) * 100}%` }} /></div>
            <div className="flex justify-between">
              <PillBtn disabled={i === 0} onClick={() => setI(i - 1)} data-testid="learn-prev"><ChevronLeft className="w-4 h-4" /> Back</PillBtn>
              {last ? <PillBtn primary onClick={complete} data-testid="learn-complete"><CheckCircle2 className="w-4 h-4" /> Complete course</PillBtn>
                : <PillBtn primary onClick={next} data-testid="learn-next">Next <ChevronRight className="w-4 h-4" /></PillBtn>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
